/**
 * Returns the offset in milliseconds between the given timezone and UTC
 * for a specific instant, using Intl.DateTimeFormat.formatToParts to avoid
 * locale-dependent string parsing (which breaks on Node 18+ ICU changes).
 *
 * @param {Date} date - The instant to evaluate
 * @param {string} timeZone - IANA timezone identifier
 * @returns {number} Offset in ms (positive = ahead of UTC)
 */
function getTimeZoneOffsetMs(date, timeZone) {
    const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: 'numeric', second: 'numeric',
        hour12: false,
    });
    const parts = Object.fromEntries(
        fmt.formatToParts(date).map(p => [p.type, parseInt(p.value, 10)])
    );
    const asUTC = Date.UTC(
        // parts.month is 1-12 (Intl); Date.UTC expects a 0-11 month index, hence -1.
        parts.year, parts.month - 1, parts.day,
        parts.hour % 24, parts.minute, parts.second
    );
    return asUTC - date.getTime();
}

/**
 * Computes the UTC epoch (seconds) of station-local midnight for the current
 * station-local date. Uses a two-pass approach to handle DST transitions:
 * the offset at "now" gives the target calendar date, then the offset is
 * re-derived at the candidate midnight instant to correct for transitions.
 *
 * @param {Date} now - Current instant
 * @param {string} stationTimeZone - IANA timezone identifier (e.g. "America/Los_Angeles")
 * @returns {number} Epoch seconds of station-local midnight (start of day)
 */
export function getStationLocalMidnightEpoch(now, stationTimeZone) {
    const offsetMs = getTimeZoneOffsetMs(now, stationTimeZone);

    // Derive station-local calendar date using formatToParts
    const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: stationTimeZone,
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour12: false,
    });
    const parts = Object.fromEntries(
        fmt.formatToParts(now).map(p => [p.type, parseInt(p.value, 10)])
    );
    const targetYear = parts.year;
    // Intl.formatToParts returns month as 1-12, but Date.UTC(year, monthIndex, ...)
    // expects a 0-11 month index, so convert here (e.g. July: parts.month=7 -> 6).
    const targetMonth = parts.month - 1;
    const targetDate = parts.day;

    // First approximation of station-local midnight using offset-at-now
    const candidateMidnight = new Date(Date.UTC(targetYear, targetMonth, targetDate) - offsetMs);

    // Re-derive offset at the candidate instant (handles DST transition days)
    const correctedOffsetMs = getTimeZoneOffsetMs(candidateMidnight, stationTimeZone);

    // Recompute using corrected offset but the original target date
    return Math.floor((Date.UTC(targetYear, targetMonth, targetDate) - correctedOffsetMs) / 1000);
}

/**
 * UTC calendar-day start (epoch seconds) for the instant's UTC date.
 * Used as the safe fallback when no usable timezone is available.
 *
 * @param {Date} now - Current instant
 * @returns {number} Epoch seconds of UTC midnight
 */
export function getUtcMidnightEpoch(now) {
    return Math.floor(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) / 1000);
}

/**
 * Best-effort IANA timezone of the runtime (browser) the page is running in.
 * SCC runs as an iframe on station-local machines, so for the dominant on-station
 * case this equals the station timezone and is available on the FIRST paint —
 * unlike the API-response stationTimeZone, which only arrives after a round trip.
 *
 * @returns {string|undefined} IANA tz id, or undefined if it cannot be resolved
 */
export function getBrowserTimeZone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    } catch (e) {
        return undefined;
    }
}

/**
 * Resilient calendar-day start for the RTS default range. Returns epoch seconds
 * for the start of "today" in the given timezone, and NEVER throws: on an empty
 * or invalid timezone (or any Intl failure) it falls back to UTC midnight rather
 * than letting a RangeError break the RTS page load.
 *
 * @param {Date} now - Current instant
 * @param {string} [timeZone] - IANA tz id (already alias-normalized by the caller)
 * @returns {number} Epoch seconds of the calendar-day start
 */
export function getCalendarDayStartEpoch(now, timeZone) {
    if (timeZone) {
        try {
            return getStationLocalMidnightEpoch(now, timeZone);
        } catch (e) {
            // Invalid/unsupported tz id (e.g. a malformed stationTimeZone from the
            // API) — log and fall back to UTC midnight rather than letting a
            // RangeError break the RTS page load.
            console.warn("getCalendarDayStartEpoch: invalid timeZone '" + timeZone + "', falling back to UTC midnight", e);
        }
    }
    return getUtcMidnightEpoch(now);
}

/**
 * UTC epoch (seconds) of station-local END-of-day (23:59:59) for the current
 * station-local date. Computed as (start-of-tomorrow) - 1 second, using the same
 * DST-safe two-pass approach as getStationLocalMidnightEpoch.
 *
 * "Tomorrow's midnight" is derived by anchoring 26h forward from TODAY's
 * station-local midnight, then re-resolving that anchor to its own station-local
 * midnight (not by adding 24h to `now`). Adding 24h to the instant is wrong on a
 * DST fall-back day (25h long): from 00:30 local, now+24h is still 23:30 the SAME
 * day, so end-of-day would come out a day early. The 26h anchor is always greater
 * than the longest possible civil day (25h) and less than 2 days, so it lands
 * unambiguously inside tomorrow for ANY UTC offset and BOTH DST directions
 * (23h spring-forward and 25h fall-back); minus one second yields today's 23:59:59.
 *
 * @param {Date} now - Current instant
 * @param {string} stationTimeZone - IANA timezone identifier
 * @returns {number} Epoch seconds of station-local 23:59:59
 */
export function getStationLocalEndOfDayEpoch(now, stationTimeZone) {
    // Anchor into the NEXT station-local calendar day by starting from today's
    // station-local midnight and advancing 26 hours. 26h is always > the longest
    // possible day (25h on a DST fall-back day) and < 2 days, so the anchor lands
    // unambiguously inside tomorrow for ANY UTC offset (incl. UTC+13/+14) and any DST
    // transition. getStationLocalMidnightEpoch(anchor) is then tomorrow's midnight;
    // minus one second is today's 23:59:59.
    //
    // (Adding 24h to `now` directly is wrong on a fall-back day: from 00:30 local the
    // day is 25h long, so now+24h is still today. Advancing from today's midnight by
    // 26h avoids that. Anchoring on a fixed UTC hour is wrong for far-east-of-UTC zones,
    // where that instant can map to two local days ahead.)
    const todayMidnight = getStationLocalMidnightEpoch(now, stationTimeZone);
    const nextDayAnchor = new Date((todayMidnight + 26 * 60 * 60) * 1000);
    return getStationLocalMidnightEpoch(nextDayAnchor, stationTimeZone) - 1;
}

/**
 * UTC end-of-day (epoch seconds) for the instant's UTC date. Safe fallback
 * for getCalendarDayEndEpoch when no usable timezone is available.
 *
 * @param {Date} now - Current instant
 * @returns {number} Epoch seconds of UTC 23:59:59
 */
export function getUtcEndOfDayEpoch(now) {
    return Math.floor(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1) / 1000
    ) - 1;
}

/**
 * Resilient calendar-day END for the RTS default range. Returns epoch seconds
 * for 23:59:59 in the given timezone; NEVER throws (mirrors getCalendarDayStartEpoch
 * fallback behavior). Introduced for exact-wording compliance:
 * "current calendar day (00:00–23:59 local station time)".
 *
 * @param {Date} now - Current instant
 * @param {string} [timeZone] - IANA tz id
 * @returns {number} Epoch seconds of calendar-day end
 */
export function getCalendarDayEndEpoch(now, timeZone) {
    if (timeZone) {
        try {
            return getStationLocalEndOfDayEpoch(now, timeZone);
        } catch (e) {
            // Invalid/unsupported tz id — log and fall back to UTC end-of-day
            // (mirrors getCalendarDayStartEpoch); never throws so the RTS page loads.
            console.warn("getCalendarDayEndEpoch: invalid timeZone '" + timeZone + "', falling back to UTC end-of-day", e);
        }
    }
    return getUtcEndOfDayEpoch(now);
}



// WEBPACK FOOTER //
// ./src/commons/utils/stationTime.js